import React, { useMemo } from 'react';
import CreatableSelect from 'react-select/creatable';
import { MultiValue } from 'react-select';
import { creatableMultiSelectStyles, type CreatableOption } from './creatableSelectStyles';

export type SubjectNameOption = CreatableOption;

interface SubjectNameMultiSelectProps {
    options: SubjectNameOption[];
    value: SubjectNameOption[] | null;
    onChange: (next: SubjectNameOption[] | null) => void;
    placeholder?: string;
    disabled?: boolean;
    isLoading?: boolean;
    isSmall?: boolean;
}

const SubjectNameMultiSelect: React.FC<SubjectNameMultiSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = 'English Language',
    disabled = false,
    isLoading = false,
    isSmall = false,
}) => {
    const mergedOptions = useMemo<SubjectNameOption[]>(() => {
        const seen = new Set(options.map((o) => o.value.toLowerCase()));
        const extras = (value ?? [])
            .filter((opt) => !seen.has(opt.value.toLowerCase()))
            .map((opt) => ({ value: opt.value, label: opt.label }));
        return [...options, ...extras];
    }, [options, value]);

    const handleChange = (next: MultiValue<SubjectNameOption>) => {
        if (!next || next.length === 0) {
            onChange(null);
            return;
        }
        onChange(next.map((opt) => ({ value: opt.value, label: opt.label })));
    };

    const handleCreate = (inputValue: string) => {
        const trimmed = inputValue.trim();
        if (!trimmed) return;
        const current = value ?? [];
        if (current.some((o) => o.value.toLowerCase() === trimmed.toLowerCase())) return;
        onChange([...current, { value: trimmed, label: trimmed }]);
    };

    return (
        <CreatableSelect<SubjectNameOption, true>
            isMulti
            options={mergedOptions}
            value={value ?? []}
            onChange={handleChange}
            onCreateOption={handleCreate}
            isDisabled={disabled || isLoading}
            placeholder={isLoading ? 'Loading subjects…' : placeholder}
            styles={creatableMultiSelectStyles(isSmall)}
            classNamePrefix="subject-name-select"
            formatCreateLabel={(input) => `Add "${input}"`}
            isClearable
        />
    );
};

export default SubjectNameMultiSelect;
